// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN REWARDS PAGE                            ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Gift,
  Search,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Save,
  RefreshCw,
  DollarSign,
  Star,
  AlertCircle,
  Package,
  Eye,
  EyeOff,
  Hash,
  Tag,
  ShoppingBag,
  Clock,
  CheckCircle
} from 'lucide-react'
import { RewardsAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function RewardsPage() {
  const { hasPermission } = useAdmin()
  const [activeTab, setActiveTab] = useState('catalog')

  // Catalog state
  const [rewards, setRewards] = useState([])
  const [loadingRewards, setLoadingRewards] = useState(true)

  // Redemptions state
  const [redemptions, setRedemptions] = useState([])
  const [loadingRedemptions, setLoadingRedemptions] = useState(false)

  // Common state
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingReward, setEditingReward] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (activeTab === 'catalog') {
      fetchRewards()
    } else {
      fetchRedemptions()
    }
    fetchStats()
  }, [activeTab])

  const fetchRewards = async () => {
    setLoadingRewards(true)
    setError(null)

    try {
      const res = await RewardsAdminAPI.getRewards()
      if (res.success) {
        setRewards(res.data.rewards)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load rewards catalog')
    } finally {
      setLoadingRewards(false)
    }
  }

  const fetchRedemptions = async () => {
    setLoadingRedemptions(true)
    setError(null)

    try {
      const res = await RewardsAdminAPI.getRedemptions()
      if (res.success) {
        setRedemptions(res.data.redemptions)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load redemptions')
    } finally {
      setLoadingRedemptions(false)
    }
  }

  const fetchStats = async () => {
    try {
      const res = await RewardsAdminAPI.getStats()
      if (res.success) {
        setStats(res.data)
      }
    } catch (err) {
      console.error('Failed to load stats')
    }
  }

  const handleSaveReward = async (rewardData) => {
    setProcessing(true)
    try {
      const res = await RewardsAdminAPI.saveReward(rewardData)
      if (res.success) {
        fetchRewards()
        setShowModal(false)
        setEditingReward(null)
      }
    } catch (err) {
      console.error('Failed to save reward')
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteReward = async (rewardId) => {
    if (!confirm('Are you sure you want to delete this reward?')) return

    try {
      const res = await RewardsAdminAPI.deleteReward(rewardId)
      if (res.success) {
        fetchRewards()
      }
    } catch (err) {
      console.error('Failed to delete reward')
    }
  }

  const handleToggleStatus = async (rewardId, currentStatus) => {
    try {
      const res = await RewardsAdminAPI.toggleStatus(rewardId, !currentStatus)
      if (res.success) {
        fetchRewards()
      }
    } catch (err) {
      console.error('Failed to toggle status')
    }
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const filteredRewards = rewards.filter(reward => {
    const matchesSearch = !search ||
      reward.name.toLowerCase().includes(search.toLowerCase()) ||
      reward.description?.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = !categoryFilter || reward.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const categories = [...new Set(rewards.map(r => r.category).filter(Boolean))]

  const getCategoryColor = (category) => {
    const colors = {
      'gift_cards': 'bg-purple-500/20 text-purple-400',
      'merchandise': 'bg-blue-500/20 text-blue-400',
      'travel': 'bg-emerald-500/20 text-emerald-400',
      'cashback': 'bg-green-500/20 text-green-400',
      'experiences': 'bg-orange-500/20 text-orange-400'
    }
    return colors[category] || 'bg-slate-500/20 text-slate-400'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Rewards Program</h1>
          <p className="text-slate-400 mt-1">Manage rewards catalog and redemptions</p>
        </div>
        {hasPermission('rewards.edit') && activeTab === 'catalog' && (
          <button
            onClick={() => {
              setEditingReward(null)
              setShowModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Reward
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Rewards</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.totalRewards)}</p>
              </div>
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-violet-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Redemptions</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.totalRedemptions)}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Points Redeemed</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.pointsRedeemed)}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Pending Fulfillment</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.pendingFulfillment)}</p>
              </div>
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'catalog'
              ? 'bg-violet-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          Rewards Catalog
        </button>
        <button
          onClick={() => setActiveTab('redemptions')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'redemptions'
              ? 'bg-violet-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          Redemptions
        </button>
      </div>

      {/* Catalog Tab */}
      {activeTab === 'catalog' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search rewards..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                ))}
              </select>

              <button
                onClick={fetchRewards}
                className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Rewards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loadingRewards ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="col-span-full flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <p className="text-slate-400">{error}</p>
                </div>
              </div>
            ) : filteredRewards.length === 0 ? (
              <div className="col-span-full text-center py-12 text-slate-400">
                No rewards found
              </div>
            ) : (
              filteredRewards.map((reward) => (
                <motion.div
                  key={reward.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden"
                >
                  {/* Reward Image */}
                  <div className="h-40 bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
                    {reward.image_url ? (
                      <img src={reward.image_url} alt={reward.name} className="h-full w-full object-cover" />
                    ) : (
                      <Gift className="w-16 h-16 text-violet-400" />
                    )}
                  </div>

                  <div className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-white">{reward.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        reward.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {reward.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">{reward.description}</p>

                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        <span className="text-white font-bold">{formatNumber(reward.points_required)} pts</span>
                      </div>
                      {reward.category && (
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getCategoryColor(reward.category)}`}>
                          {reward.category.replace('_', ' ')}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-sm text-slate-400 mb-4">
                      <span>Stock: {reward.inventory ?? 'Unlimited'}</span>
                      <span>Redeemed: {reward.times_redeemed || 0}</span>
                    </div>

                    {hasPermission('rewards.edit') && (
                      <div className="flex gap-2 pt-4 border-t border-slate-700">
                        <button
                          onClick={() => handleToggleStatus(reward.id, reward.is_active)}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                            reward.is_active
                              ? 'bg-slate-700 hover:bg-slate-600 text-white'
                              : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400'
                          }`}
                        >
                          {reward.is_active ? (
                            <>
                              <EyeOff className="w-4 h-4" />
                              Disable
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4" />
                              Enable
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingReward(reward)
                            setShowModal(true)
                          }}
                          className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteReward(reward.id)}
                          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      )}

      {/* Redemptions Tab */}
      {activeTab === 'redemptions' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          {loadingRedemptions ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <p className="text-slate-400">{error}</p>
              </div>
            </div>
          ) : redemptions.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No redemptions yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 bg-slate-900/50">
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">User</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Reward</th>
                    <th className="text-right py-4 px-4 text-sm font-medium text-slate-400">Points</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-left py-4 px-4 text-sm font-medium text-slate-400">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((redemption) => (
                    <tr key={redemption.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-white font-medium">{redemption.first_name} {redemption.last_name}</p>
                          <p className="text-slate-500 text-sm">{redemption.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-white">{redemption.reward_name}</p>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-yellow-400 font-semibold">{formatNumber(redemption.points_spent)}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                          redemption.status === 'fulfilled' ? 'bg-emerald-500/20 text-emerald-400' :
                          redemption.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {redemption.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-400 text-sm">
                        {formatDate(redemption.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reward Modal */}
      <AnimatePresence>
        {showModal && (
          <RewardFormModal
            reward={editingReward}
            onClose={() => {
              setShowModal(false)
              setEditingReward(null)
            }}
            onSave={handleSaveReward}
            processing={processing}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Reward Form Modal Component
function RewardFormModal({ reward, onClose, onSave, processing }) {
  const [formData, setFormData] = useState({
    name: reward?.name || '',
    description: reward?.description || '',
    category: reward?.category || 'gift_cards',
    points_required: reward?.points_required || 1000,
    inventory: reward?.inventory || '',
    image_url: reward?.image_url || '',
    value: reward?.value || '',
    is_active: reward?.is_active ?? true
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...formData,
      id: reward?.id
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {reward ? 'Edit Reward' : 'Add Reward'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="Amazon Gift Card"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 resize-none"
                placeholder="Reward description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="gift_cards">Gift Cards</option>
                  <option value="merchandise">Merchandise</option>
                  <option value="travel">Travel</option>
                  <option value="cashback">Cashback</option>
                  <option value="experiences">Experiences</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Points Required</label>
                <div className="relative">
                  <Star className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    value={formData.points_required}
                    onChange={(e) => setFormData({ ...formData, points_required: parseInt(e.target.value) })}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Value ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Inventory</label>
                <div className="relative">
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    value={formData.inventory}
                    onChange={(e) => setFormData({ ...formData, inventory: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Unlimited"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Image URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="https://..."
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 bg-slate-900/50 border border-slate-600 rounded"
              />
              <label htmlFor="is_active" className="text-slate-300">Active (visible to customers)</label>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-violet-500 hover:bg-violet-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Reward
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
